import {Injectable} from '@angular/core';
import {ApiService} from '../api/api.service';
import {map, tap} from 'rxjs/operators';
import {BehaviorSubject, Observable} from 'rxjs';
import {decodeJWT, normalize, sortAsc, webViewFinder} from '@app/utils';
import {Router} from '@angular/router';
import { LabsQuery } from '@app/services/labs/labs.query';
import { LabsStore } from '@app/services/labs/labs.store';
import { AuthService } from '@app/services/auth/auth.service';
import { Logger } from '@app/services/logger/logger.service';
import { LabVisit, LabVisitMetadata, RecordList } from '@app/services/labs/lab.model';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Platform } from '@ionic/angular';
import { FileOpener } from '@awesome-cordova-plugins/file-opener/ngx';

export class Filters {
  search?: string;
  searchText?: string;
  searchRange?: [Date, Date];
  testTypes?: string;
  person?: string;

  constructor(
      search: string,
      searchText: string,
      testTypes: string,
      person: string,
      searchRange?: [Date, Date],
    ) {
  
    this.search = search || '';
    this.searchText = searchText || '';
    this.testTypes = testTypes || '';
    this.person = person || '';

  }
} 

@Injectable({
  providedIn: 'root'
})
export class LabsService {
  userList: LabVisitMetadata[];
  filteredList: LabVisitMetadata[] | null;
  currentLab: { labID: string, date: string, key?: string };
  availableTypes: Set<string>;
  otherLabSourcesCount = 0;

  private _testTypeFilter = '';
  get testTypeFilter() {
    const filter = this._testTypeFilter;
    this._testTypeFilter = '';
    return filter;
  }

  set testTypeFilter(type: string) {
    this._testTypeFilter = type;
  }

  private _filters: Filters = {
    search: '',
    searchText: '',
    testTypes: '',
    person: '',
  };
  labCache: Record<string, LabVisit> = {};

  private readonly labVisits = new BehaviorSubject<LabVisitMetadata[]>(null);
  readonly labVisits$ = this.labVisits.asObservable().pipe(
    map(resultList => resultList?.filter(item => !item.hidden)),
    tap(resultList => {
      this.userList = [];
      this.userList = resultList;
      if (!resultList) { return; }

      this.availableTypes = new Set(resultList
        .flatMap(lab => lab.orders)
        .map(order => {
          if (order.test_types) {
            return order.test_types.toLowerCase()
          }
        })
        .sort(sortAsc)
      );
    })
  ).subscribe();

  private readonly hiddenLabs = new BehaviorSubject([]);
  readonly hiddenLabs$ = this.hiddenLabs.asObservable();

  private readonly locations = new BehaviorSubject([]);
  readonly locations$ = this.locations.asObservable().pipe(
    map(locations => locations
      // loop through list of laboratorioes
      .map(lab => ({
        ...lab,
        // return each object with its name cleaned
        name: lab.name
          .replace(/[$*@]/g, '') // remove junk characters
      }))
    )
  );

  private readonly records = new BehaviorSubject<RecordList>({ pending: [], processed: [] });
  readonly records$ = this.records.asObservable().pipe(
    map(records => {
      records.pending = records.pending
        // loop through list of records
        .map(record => ({
          ...record,
          // return each object with its name cleaned
          laboratory: record.laboratory
            .replace(/[$*@]/g, '') // remove junk characters
        }));

      records.processed = records.processed
        // loop through list of records
        .map(record => ({
          ...record,
          // return each object with its name cleaned
          laboratory: record.laboratory
            .replace(/[$*@]/g, '') // remove junk characters
        }));

      return records;
    })
  );

  constructor(
    private store: LabsStore,
    private query: LabsQuery,
    private api: ApiService,
    private router: Router,
    private auth: AuthService,
    public platform: Platform,
    private fileOpener: FileOpener) {
    this.auth.isLoggedIn$.subscribe((status) => {
      status ? this.load() : this.clear();
    });
  }

  load() {
    this.refresh();
  }

  refresh() {
    this.api.listResults().subscribe(
      (list) => this.labVisits.next(list),
      () => this.labVisits.next([])
    );
    this.api.listOfLabs().subscribe(
      list => this.locations.next(list),
      () => this.locations.next([])
    );
    this.api.listRecords().subscribe(
      list => this.records.next(list),
      () => this.records.next({ pending: [], processed: [] })
    );

    this.api.getOtherLabSourcesCount().subscribe(countsByUser => {
      this.otherLabSourcesCount = Object.values(countsByUser)
        .reduce((total, count) => total + count, 0);
    });

    // TODO: Rename this endpoint function. listOfLabs and listOfLabsObscured
    // are totally different things but the names imply they are similar
    this.api.listOfLabsObscured().subscribe((res: any) => this.hiddenLabs.next(res));
  }

  clear() {
    this.labVisits.next(null);
    this.locations.next([]);
    this.records.next({ pending: [], processed: [] });
    this.hiddenLabs.next([]);
  }

  // TODO: stop turning observables into promises :)
  async getLabData(fullLabID): Promise<LabVisit> {
    let labID, date, key;
    try {
      const {lab_record_id, results_id} = decodeJWT(fullLabID);
      labID = lab_record_id;
      date = results_id;
      key = fullLabID;
    } catch (e) {
      [labID, date] = fullLabID.split('&');
    }

    // mark accessed labs as read locally since the list isn't being constantly refreshed now
    const currentLabIndex = this.userList?.findIndex(item => item.lab_record_id.join(',') === labID && item.visit_date === date);
    if (currentLabIndex > -1) {
      this.userList[currentLabIndex].read = true;
    }

    // TODO: find a better way to keep track of this `currentLab`...
    this.currentLab = { labID, date, key };
    const cachedLab = this.labCache[key || `${labID}&${date}`];

    // if (cachedLab) {
    //   return cachedLab;
    // } else if (key) {
    if (key) {
      return await this.api.getSharedLabResult(key).toPromise()
        .catch(() => void this.router.navigate(['/invalidSharedLab']));
    } else if (this.auth.isLoggedIn()) {
      return await this.api.getLabResult(labID, date).toPromise().catch(error => {
        Logger.error(`Problem accessing lab-result:${fullLabID}`, error);
        this.router.navigate(['/home']);
      }) as LabVisit;
    } else {
      Logger.error(`Unauthenticated access to lab-result:${fullLabID}`, {});
      this.router.navigate(['/login']);
    }
  }

  createShareKey({ email, exp }, filter_list): Observable<any> {
    const { labID, date } = this.currentLab;
    let lab_tests = [];
 
    filter_list.forEach(value => {
      if (value.filter == true) {
        lab_tests.push({
          metadata: value.metadata,
          lab_id: value.lab_id
        });
      }  
    });

    const data = {
      lab_record_id: labID,
      results_id: date,
      destination_email: email,
      lab_tests: lab_tests,
      exp: exp * 24
    };
    
    return this.api.shareLabResult(data);
  }

  saveLabData(labID, labData) {
    if (this.labCache[labID] = labData) {
        console.warn('lab data saved for labID: ' + labID, labData)
    }
  }

  // TODO: this `cache` can be made more persistent, eg. save to local storage
  async getLabPDF() {
    const { labID, date, key } = this.currentLab;
    const cachedLab = this.labCache[key || `${labID}&${date}`];
    const cachedPDF = cachedLab.pdf;
    const {lab_name, results_date} = cachedLab.visit[0].heading;

    const getLabAction = key ? this.api.getSharedLabResultPDF(key, 1) : this.api.getLabResultPDF(labID, date, 1);

    this.api.getLabResultPDF(labID, date, 1).toPromise().then((pdfStream) => {
      console.log("pdfStream", pdfStream);
      this.saveBlob(pdfStream, {lab_name, results_date});
    }).catch((errorCatch)=> {
      console.log("errorCatch", errorCatch);
    })

    // TODO: this can be improved with observables
    if (!cachedPDF) {
      return getLabAction.toPromise().then(pdfStream => {
        cachedLab.pdf = pdfStream.body;
        this.saveBlob(pdfStream.body, {lab_name, results_date});
        return Promise.resolve();
      }).catch(Promise.reject);
    } else {
      const success = await this.saveBlob(cachedPDF, {lab_name, results_date}).catch(null);
      return success ? Promise.resolve() : Promise.reject();
    }
  }

  private async saveBlob(blob, {lab_name, results_date}) {
    // const dateFields: [number, number, number] = results_date.split('-').map(field => Number(field));
    // const date = new Date(`${results_date}T00:00:00`);
    // const dateString = date.toLocaleDateString(`${this.lang.getActiveLang()}-US`);

    try {
      const isIOSApp = webViewFinder();

      if (this.platform.is('android')) {

        // Ionic abstracts the native file reader for some reason, need to taget it at its root.
        const reader = new FileReader();

        reader.onload = () => {
          let base64data = reader.result as string;
          console.log("base64data",base64data);
          Filesystem.writeFile({
              data: base64data,
              path: `patientlynk/${lab_name || 'Lab'}_${results_date || ''}.pdf`,
              directory: Directory.Documents,
              recursive: true
          }).then((result) =>{
            this.fileOpener.showOpenWithDialog(result.uri, 'application/pdf')
          });
        };

        reader.onerror = () => {
          console.log("reader error", reader.result);
        };

        reader.readAsDataURL(blob);

      } else if (this.platform.is('ios')) {

        // Ionic abstracts the native file reader for some reason, need to taget it at its root.
        const reader = new FileReader();

        reader.onload = () => {
          let base64data = reader.result as string;
          console.log("base64data",base64data);
          Filesystem.writeFile({
              data: base64data,
              path: `patientlynk/${lab_name || 'Lab'}_${results_date || ''}.pdf`,
              directory: Directory.Documents,
              recursive: true
          }).then((result) =>{
            this.fileOpener.showOpenWithDialog(result.uri, 'application/pdf')
          })          
        };

        reader.onerror = () => {
          console.log("reader error", reader.result);
        };

        reader.readAsDataURL(blob);

      } else {
        console.log(blob.body, {lab_name, results_date});
        
        const a = document.createElement('a');
        const url = window.URL.createObjectURL(blob.body);
        document.body.appendChild(a);
        a.style.display = 'none';
        a.href = url;
        a.download = `${lab_name || 'Lab'}_${results_date || ''}.pdf`.replace(/ /g, '_');
        a.click();
      }
    } catch {
      return false;
    }

    return true;
  }

  filter() {
    console.log("filter");
    
    const {searchText, searchRange, testTypes, person} = this._filters;
    
    this.filteredList = this.userList
      ?.filter(item => { // text filter
        if (!searchText) {
          return true;
        }
        const visitData = normalize(item.orders.flatMap((order: any) => Object.values(order)).join());
        return visitData.includes(normalize(searchText));
      })
      .filter(item => { // type filter
        if (!testTypes) {
          return true;
        }
        const visitData = normalize(item.orders.flatMap((order: any) => Object.values(order)).join());
        return visitData.includes(normalize(testTypes));
      })
      .filter(item => { // date filter
        // TODO: search for string piped date
        if (!searchRange || !searchRange.length) {
          return true;
        }

        const labDate = new Date(item.visit_date);
        const [fromDate = new Date(0), toDate = new Date(0)] = searchRange;

        let normalizeLabDate = new Date( labDate.getTime() - labDate.getTimezoneOffset() * -60000 )
        let normalizeFromDate = new Date(fromDate.toDateString())
        let normalizeToDate = new Date(toDate.toDateString())
        
        return normalizeLabDate.getTime() >= normalizeFromDate.getTime() && normalizeLabDate.getTime() <= normalizeToDate.getTime();
      })
      .filter(item => {
        if (!person) {
          return true;
        }

        return item.person === person;
      }); // person filter
  }

  setFilter(newFilters: Filters) {

    console.log("newFilters", newFilters);
    

    this._filters = {
      ...this._filters,
      ...newFilters
    };

    this.filter();
  }

  clearFilter() {
    console.log("clearFilter");
    this._filters = {
      search: '',
      searchText: '',
      testTypes: '',
      person: '',
    };
    this.filter();    
  }
}
